import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { Shield, Users, User, Settings, Key, Lock } from "lucide-react";
import type { Project, User as UserType } from "@shared/schema";

interface RoleProjectAccess {
  id: number;
  role: string;
  projectId: string;
  accessLevel: string;
  grantedBy: number;
  grantedAt: Date;
}

interface RoleDefaultStatus {
  role: string;
  defaultStatus: string;
  defaultPriority: string;
  autoAssignProjects: boolean;
  defaultAccessLevel: string;
}

interface UserWithAccess extends UserType {
  hasAccess: boolean;
  accessLevel: string;
  source: string;
  override?: boolean;
  individualAccess?: string;
  canBeModified: boolean;
}

export function UserAccessSettings() {
  const { toast } = useToast();
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<string>("team");
  
  // Fetch projects
  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ['/api/projects'],
  });
  
  // Fetch all users
  const { data: users = [] } = useQuery<UserType[]>({
    queryKey: ['/api/users'],
  });
  
  // Fetch role defaults
  const { data: roleDefaults = [] } = useQuery<RoleDefaultStatus[]>({
    queryKey: ['/api/role-defaults'],
  });
  
  // Fetch role project access
  const { data: roleAccess = [] } = useQuery<RoleProjectAccess[]>({
    queryKey: ['/api/role-access'],
  });
  
  // Fetch users with access for selected project
  const { data: usersWithAccess = [], isLoading: isLoadingUsers } = useQuery<UserWithAccess[]>({
    queryKey: ['/api/projects', selectedProject, 'all-users-access'],
    enabled: !!selectedProject,
  });
  
  // Update role default status mutation
  const updateRoleDefaultsMutation = useMutation({
    mutationFn: async (data: { role: string; updates: Partial<RoleDefaultStatus> }) => {
      const res = await apiRequest('PATCH', `/api/role-defaults/${data.role}`, data.updates);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/role-defaults'] });
      toast({
        title: "Success",
        description: "Role defaults updated successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update role defaults",
        variant: "destructive",
      });
    },
  });
  
  // Set role project access mutation
  const setRoleAccessMutation = useMutation({
    mutationFn: async (data: { role: string; projectId: string; accessLevel: string }) => {
      const res = await apiRequest('POST', '/api/role-access', data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/role-access'] });
      queryClient.invalidateQueries({ queryKey: ['/api/projects', selectedProject, 'all-users-access'] });
      toast({
        title: "Success",
        description: "Role access updated successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update role access",
        variant: "destructive",
      });
    },
  });
  
  // Set individual user access mutation
  const setUserAccessMutation = useMutation({
    mutationFn: async (data: { userId: number; projectId: string; accessLevel: string }) => {
      const res = await apiRequest('POST', '/api/user-project-access', data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/user-project-access'] });
      queryClient.invalidateQueries({ queryKey: ['/api/projects', selectedProject, 'all-users-access'] });
      toast({
        title: "Success",
        description: "User access updated successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update user access",
        variant: "destructive",
      });
    },
  });
  
  const roles = ['admin', 'team', 'external', 'guest'];
  const accessLevels = ['full', 'read', 'none'];
  const statusOptions = ['active', 'pending', 'on-hold', 'completed', 'cancelled'];
  const priorityOptions = ['high', 'medium', 'low'];
  
  const getRoleDefaults = (role: string) => {
    return roleDefaults.find(r => r.role === role) || {
      role,
      defaultStatus: 'pending',
      defaultPriority: 'medium',
      autoAssignProjects: false,
      defaultAccessLevel: 'none'
    };
  };
  
  const getRoleAccess = (role: string, projectId: string) => {
    return roleAccess.find(r => r.role === role && r.projectId === projectId);
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <Shield className="w-5 h-5" />
        <h3 className="text-lg font-semibold">User Access Control</h3>
      </div>
      
      <Tabs defaultValue="role-defaults" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="role-defaults">
            <Settings className="w-4 h-4 mr-2" />
            Group Role Defaults
          </TabsTrigger>
          <TabsTrigger value="project-access">
            <Lock className="w-4 h-4 mr-2" />
            Group Project Access
          </TabsTrigger>
          <TabsTrigger value="individual-access">
            <User className="w-4 h-4 mr-2" />
            Individual Permissions
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="role-defaults" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Default Settings by Group Role</CardTitle>
              <CardDescription>
                Configure default status, priority and access levels for each group role
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {roles.map(role => {
                const defaults = getRoleDefaults(role);
                return (
                  <div key={role} className="p-4 border rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium capitalize flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        {role}
                      </h4>
                      <Badge variant={role === 'admin' ? 'default' : 'secondary'}>
                        {role === 'admin' ? 'Full Access' : defaults.defaultAccessLevel}
                      </Badge>
                    </div>
                    
                    {role !== 'admin' && (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Default Status</Label>
                            <Select
                              value={defaults.defaultStatus}
                              onValueChange={(value) => 
                                updateRoleDefaultsMutation.mutate({
                                  role,
                                  updates: { defaultStatus: value }
                                })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {statusOptions.map(status => (
                                  <SelectItem key={status} value={status}>
                                    {status}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div className="space-y-2">
                            <Label>Default Priority</Label>
                            <Select
                              value={defaults.defaultPriority}
                              onValueChange={(value) => 
                                updateRoleDefaultsMutation.mutate({
                                  role,
                                  updates: { defaultPriority: value }
                                })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {priorityOptions.map(priority => (
                                  <SelectItem key={priority} value={priority}>
                                    {priority}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex items-center space-x-2">
                            <Switch
                              id={`auto-assign-${role}`}
                              checked={defaults.autoAssignProjects}
                              onCheckedChange={(checked) => 
                                updateRoleDefaultsMutation.mutate({
                                  role,
                                  updates: { autoAssignProjects: checked }
                                })
                              }
                            />
                            <Label htmlFor={`auto-assign-${role}`}>
                              Auto-assign new projects
                            </Label>
                          </div>
                          
                          <div className="space-y-2">
                            <Label>Default Access Level</Label>
                            <Select
                              value={defaults.defaultAccessLevel}
                              onValueChange={(value) => 
                                updateRoleDefaultsMutation.mutate({
                                  role,
                                  updates: { defaultAccessLevel: value }
                                })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {accessLevels.map(level => (
                                  <SelectItem key={level} value={level}>
                                    {level}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
          
          {/* Apply to Existing Projects Button */}
          <Card>
            <CardHeader>
              <CardTitle>Apply to Existing Projects</CardTitle>
              <CardDescription>
                Apply current auto-assign settings to all existing projects. This will grant access to projects for roles with "Auto-assign new projects" enabled.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={async () => {
                  try {
                    const response = await apiRequest('POST', '/api/admin/apply-role-defaults');
                    const result = await response.json();
                    toast({
                      title: "Success",
                      description: result.message || "Role defaults applied to existing projects"
                    });
                    // Refresh role access data
                    queryClient.invalidateQueries({ queryKey: ['/api/role-access'] });
                    queryClient.invalidateQueries({ queryKey: ['/api/projects'] });
                  } catch (error) {
                    toast({
                      title: "Error",
                      description: "Failed to apply role defaults to existing projects",
                      variant: "destructive"
                    });
                  }
                }}
                className="w-full sm:w-auto"
              >
                Apply Defaults to All Projects
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="project-access" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Group Project Access by Role</CardTitle>
              <CardDescription>
                Set group role-based access levels for specific projects (overrides individual permissions)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Select Project</Label>
                <Select value={selectedProject} onValueChange={setSelectedProject}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a project..." />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(project => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.projectName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedProject && (
                <div className="space-y-3">
                  {roles.filter(r => r !== 'admin').map(role => {
                    const access = getRoleAccess(role, selectedProject);
                    return (
                      <div key={role} className="flex items-center justify-between p-3 border rounded-lg">
                        <Label className="capitalize">{role}</Label>
                        <Select
                          value={access?.accessLevel || 'inherit'}
                          onValueChange={(value) => {
                            if (value !== 'inherit') {
                              setRoleAccessMutation.mutate({
                                role,
                                projectId: selectedProject,
                                accessLevel: value
                              });
                            }
                          }}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="inherit">Inherit</SelectItem>
                            {accessLevels.map(level => (
                              <SelectItem key={level} value={level}>
                                {level}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="individual-access" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Individual User Access</CardTitle>
              <CardDescription>
                Manage access permissions for individual users on specific projects
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Select Project</Label>
                <Select value={selectedProject} onValueChange={setSelectedProject}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a project..." />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(project => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.projectName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedProject && !isLoadingUsers && (
                <ScrollArea className="h-96">
                  <div className="space-y-2">
                    {(usersWithAccess as UserWithAccess[]).map((user: UserWithAccess) => (
                      <div key={user.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <div>
                            <div className="font-medium">{user.firstName} {user.lastName}</div>
                            <div className="text-sm text-muted-foreground">{user.email}</div>
                          </div>
                          <Badge variant="outline" className="ml-2">
                            {user.role}
                          </Badge>
                          {user.override && (
                            <Badge variant="destructive" className="ml-1">
                              Overridden by role
                            </Badge>
                          )}
                          {user.source === 'role' && !user.override && (
                            <Badge variant="secondary" className="ml-1">
                              From role
                            </Badge>
                          )}
                        </div>
                        
                        <Select
                          value={user.individualAccess || user.accessLevel}
                          onValueChange={(value) => {
                            setUserAccessMutation.mutate({
                              userId: user.id,
                              projectId: selectedProject,
                              accessLevel: value
                            });
                          }}
                          disabled={!user.canBeModified}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {accessLevels.map(level => (
                              <SelectItem key={level} value={level}>
                                {level}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}